/**
 * The shell's shared state — the period, the account filter, and the entry
 * sheet — published to whatever is rendered inside it.
 *
 * WHY THE SHELL OWNS THIS AND NOT THE SCREENS
 * -------------------------------------------
 * Wallet's header is a CONTEXT bar: it says which month and which account you
 * are looking at, and every screen underneath answers for that context. If
 * each screen owned its own month, switching from Home to Records would silently
 * change the period under you — which is the single most disorienting thing a
 * finance app can do. So the frame owns it, the screens read it, and moving
 * between screens preserves it.
 *
 * It is deliberately NOT in the URL. The registry generates the routes and this
 * shell must not invent query contracts on pages it does not own; a screen that
 * wants its filter in the URL (Records already does, via `?q=`/`?from=`/`?to=`)
 * can read this context and write its own params.
 *
 * FOR THE FOUR SCREEN AGENTS
 * --------------------------
 *     import { useWalletShell } from "@/wallet/shell";
 *     const { bounds, period, accountId, openEntry } = useWalletShell();
 *
 * `useWalletShell()` throws outside the shell; `useWalletShellOptional()`
 * returns `null` instead, so a screen can also be rendered by the classic
 * shell without blowing up.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { addMonths, addQuarters, addWeeks, addYears } from "date-fns";
import { getPeriodBounds, type PeriodBounds, type PeriodKey } from "@/lib/periods";
import type { EntryRecord } from "@/wallet/entry/types";

export type EntryType = "income" | "expense" | "transfer";

export interface WalletAccountOption {
  id: string;
  bankName: string;
  nickname?: string | null;
  accountType?: string | null;
  color?: string | null;
}

export interface WalletShellValue {
  /** Which period type the header is showing. */
  period: PeriodKey;
  setPeriod(next: PeriodKey): void;
  /** The date inside the period currently displayed. */
  anchor: Date;
  /** Bounds + label of the displayed period — `YYYY-MM-DD` strings. */
  bounds: PeriodBounds;
  /** Step the displayed period back (`-1`) or forward (`+1`). */
  shiftPeriod(delta: number): void;
  /** Jump back to the period containing today. */
  resetPeriod(): void;
  /** True when `anchor` is inside the period containing today. */
  isCurrentPeriod: boolean;

  /** `null` = every account. The header's account chip writes this. */
  accountId: string | null;
  setAccountId(next: string | null): void;
  /** The accounts the header loaded, so a screen need not re-fetch them. */
  accounts: WalletAccountOption[];

  /** Open the full-screen entry sheet (the centre `+`). */
  openEntry(type?: EntryType, options?: { cloneFrom?: EntryRecord; edit?: EntryRecord }): void;
  /** F5 Clone: the entry the open entry screen was cloned from, if any. */
  entryClone: EntryRecord | null;
  /** The saved record the entry screen is EDITING, if any (a PUT, not a POST). */
  entryEdit: EntryRecord | null;
  closeEntry(): void;
  entryOpen: boolean;
  entryType: EntryType;

  /** Open the search sheet with an optional seed query. */
  openSearch(seed?: string): void;
  closeSearch(): void;
  searchOpen: boolean;
}

const WalletShellContext = createContext<WalletShellValue | null>(null);

/** Step an anchor date by whole periods of the given type. */
export function shiftAnchor(period: PeriodKey, ref: Date, delta: number): Date {
  switch (period) {
    case "weekly":
      return addWeeks(ref, delta);
    case "monthly":
      return addMonths(ref, delta);
    case "quarterly":
      return addQuarters(ref, delta);
    case "halfyear":
      return addMonths(ref, delta * 6);
    case "fy":
      return addYears(ref, delta);
  }
}

export interface WalletShellProviderProps {
  children: ReactNode;
  accounts: WalletAccountOption[];
  /** Injected by tests / storybook; the shell itself starts at "monthly". */
  initialPeriod?: PeriodKey;
}

export function WalletShellProvider({
  children,
  accounts,
  initialPeriod = "monthly",
}: WalletShellProviderProps) {
  const [period, setPeriodState] = useState<PeriodKey>(initialPeriod);
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [accountId, setAccountId] = useState<string | null>(null);
  const [entryOpen, setEntryOpen] = useState(false);
  const [entryType, setEntryType] = useState<EntryType>("expense");
  const [entryClone, setEntryClone] = useState<EntryRecord | null>(null);
  const [entryEdit, setEntryEdit] = useState<EntryRecord | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);

  const bounds = useMemo(() => getPeriodBounds(period, anchor), [period, anchor]);
  const todayBounds = useMemo(() => getPeriodBounds(period, new Date()), [period]);
  const isCurrentPeriod = bounds.start === todayBounds.start;

  // Changing the period TYPE re-anchors on today, which is what every period
  // switcher in this app already does: "last week" and "last quarter" are not
  // the same offset, so carrying an offset across a type change lands you in an
  // arbitrary month.
  const setPeriod = useCallback((next: PeriodKey) => {
    setPeriodState(next);
    setAnchor(new Date());
  }, []);

  const shiftPeriod = useCallback(
    (delta: number) => setAnchor((current) => shiftAnchor(period, current, delta)),
    [period],
  );

  const resetPeriod = useCallback(() => setAnchor(new Date()), []);

  const openEntry = useCallback(
    (type: EntryType = "expense", options?: { cloneFrom?: EntryRecord; edit?: EntryRecord }) => {
      setEntryType(type);
      setEntryClone(options?.cloneFrom ?? null);
      setEntryEdit(options?.edit ?? null);
      setEntryOpen(true);
    },
    [],
  );
  const closeEntry = useCallback(() => setEntryOpen(false), []);
  const openSearch = useCallback(() => setSearchOpen(true), []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);

  const value = useMemo<WalletShellValue>(
    () => ({
      period,
      setPeriod,
      anchor,
      bounds,
      shiftPeriod,
      resetPeriod,
      isCurrentPeriod,
      accountId,
      setAccountId,
      accounts,
      openEntry,
      closeEntry,
      entryOpen,
      entryType,
      entryClone,
      entryEdit,
      openSearch,
      closeSearch,
      searchOpen,
    }),
    [
      period,
      setPeriod,
      anchor,
      bounds,
      shiftPeriod,
      resetPeriod,
      isCurrentPeriod,
      accountId,
      accounts,
      openEntry,
      closeEntry,
      entryOpen,
      entryType,
      entryClone,
      entryEdit,
      openSearch,
      closeSearch,
      searchOpen,
    ],
  );

  return <WalletShellContext.Provider value={value}>{children}</WalletShellContext.Provider>;
}

export function useWalletShell(): WalletShellValue {
  const value = useContext(WalletShellContext);
  if (!value) throw new Error("useWalletShell must be used inside <WalletShell>");
  return value;
}

/** For components that may render under EITHER shell. */
export function useWalletShellOptional(): WalletShellValue | null {
  return useContext(WalletShellContext);
}

/** Human label for an account chip. */
export function accountLabel(account: WalletAccountOption): string {
  return account.nickname?.trim() || account.bankName;
}
